import {
    EventStreamContentType,
    fetchEventSource,
} from "@microsoft/fetch-event-source";
import { inject, injectable } from "tsyringe";
import { from, Observable, of } from "rxjs";
import { HttpClient } from "src/infra/http/client";
import { autoBind } from "src/shared/decorators/autobind";
import { Environment } from "src/libs/environment";
import { replaceString } from "src/libs/replace-string";
import { delay } from "rxjs/operators";
import fakeAssistances from "src/containers/vehicular-assistance/core/__mocks__/assistances.json";

export type GetAssistancesType = {
    systemCode: string;
    policyId: string;
};

export type SendAssistanceFormType = {
    systemCode: string;
    policyId: string;
    cellphone: string;
    email: string;
    license: string;
    role: string;
    serviceCode: string;
    subServiceCode: string;
    claimType: string;
    supportLocation: {
        address: string;
        addressRef: string;
        latitude: number;
        longitude: number;
    };
    detail: Partial<{
        dashboardOn: Nullable<boolean>;
        parkingType: Nullable<string>;
        spareTire: Nullable<boolean>;
        supportPlace: Nullable<string>;
        tireLever: Nullable<boolean>;
        tireLock: Nullable<boolean>;
    }>;
};

export type AssistanceTreeType = {
    code: string;
    name: string;
    subServices: Array<{ code: string; name: string }>;
};

@injectable()
export class VehicularAssistanceRepository {
    constructor(@inject("HttpClient") private http: HttpClient) {}

    @autoBind
    getAssistances(
        data: GetAssistancesType,
        useMock = false
    ): Observable<Array<AssistanceTreeType>> {
        const headers = this.getHeaders();
        const url = VehicularAssistanceRepository.getEndpoint();
        const params = {
            "system-code": data.systemCode,
            "policy-id": data.policyId,
        };
        return useMock
            ? of(fakeAssistances)
            : this.http.getJSON(url, {
                  host: this.http.msHostEnv,
                  customHeader: headers,
                  params,
              });
    }

    @autoBind
    sendAssistanceForm(
        data: SendAssistanceFormType,
        useMock = false
    ): Observable<{ requestId: number }> {
        const headers = this.getHeaders();
        const url = VehicularAssistanceRepository.getSendAssistanceForm();
        const params = {};
        return useMock
            ? of({ requestId: 12354 }).pipe(delay(2000))
            : this.http.post(url, {
                  host: this.http.msHostEnv,
                  customHeader: headers,
                  params,
                  data,
              });
    }

    @autoBind
    requestCaseStatus(requestId: number, useMock = false): Observable<string> {
        const endpoint = replaceString(
            VehicularAssistanceRepository.getRequestCaseStatusEndpoint(),
            {
                ":requestId": requestId,
            }
        );
        const url = `${this.http.msHostEnv}/${endpoint}`;

        const eventData = new Promise<string>(async (resolve, reject) => {
            const abortController = new AbortController();
            const signal = abortController.signal;
            await fetchEventSource(url, {
                signal: signal,
                headers: this.getHeaders(true),
                async onopen(response) {
                    if (
                        response.ok &&
                        response.headers
                            .get("content-type")
                            ?.includes(EventStreamContentType)
                    ) {
                        return;
                    } else {
                        reject("Error trying to connect to service");
                        abortController.abort();
                    }
                },
                onmessage(ev) {
                    resolve(ev.data);
                    abortController.abort();
                },
                onerror(err) {
                    reject(err);
                    abortController.abort();
                },
            });
        });

        return useMock
            ? of('{"action":"Registrado","message":"Caso generado"}').pipe(
                  delay(1000)
              )
            : from(eventData);
    }

    @autoBind
    getCoverageCoordinates(): Observable<Record<string, any>> {
        // ! remove the /static part of the url because this json
        // ! is stored outside static folder
        const host = Environment.string("BLOB_STORAGE_URL").replace(
            "/static",
            ""
        );
        const url = VehicularAssistanceRepository.getCoverageEndpoint();
        return this.http.getJSON(url, { host });
    }

    @autoBind
    getHeaders(useStreamEventAcceptHeader = false): Record<string, any> {
        const extraHeader = useStreamEventAcceptHeader
            ? this.http.httpUtils.getStreamEventHeader()
            : {};
        return {
            ...this.http.httpUtils.getPrivateHeader(),
            ...this.http.httpUtils.getMsBaseHeaders(window.userIdentity, "MEP"),
            ...extraHeader,
        };
    }

    static getEndpoint(): string {
        return "ux-siniestro-autos-gen/mep/atencion-de-siniestros/v1/api/services";
    }

    static getSendAssistanceForm(): string {
        return "ux-siniestro-autos-gen/mep/atencion-de-siniestros/v1/api/requests";
    }

    static getCoverageEndpoint(): string {
        return "stsiniestrosautos/perimetro-asiste/cobertura-asiste.json";
    }

    static getRequestCaseStatusEndpoint(): string {
        return "ux-seguimiento-siniestro-autos-gen/mep/atencion-de-siniestros/v1/api/requests/:requestId/tracking";
    }
}
